import { Flex, message } from "antd";
import React, { useState, useEffect, useCallback, useRef } from "react";
import * as Sentry from "@sentry/react";
import SubHeader from "../../components/SubHeader";
import ECForm from "./components/ECForm.tsx";
import MemberPreview from "./components/MemberPreview";
import { ecType } from "./components/ec.types";
import {
  ConvertToBackendType,
  ConvertToEcType,
} from "../../../../utils/cardSetting.ts";
import { BrandLink } from "../../components/BrandLink.tsx";
import { t } from "i18next";

interface ECFormRefType {
  handleManualSubmit: () => void;
}

const ECModule: React.FC = () => {
  const ECFormRef = useRef<ECFormRefType>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formValues, setFormValues] = useState<ecType | null>(null);
  const [originalFormValues, setOriginalFormValues] = useState<ecType | null>(
    null,
  );
  const [token, setToken] = useState<string | null>(null);
  const [platform, setPlatform] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedToken = urlParams.get("token");
    const platformParam = urlParams.get("platform");

    if (encodedToken) {
      localStorage.clear();
      const decodedToken = atob(encodedToken);

      localStorage.setItem("jwt_token", decodedToken);
      localStorage.setItem(
        "platform",
        platformParam || "shopify" || "shopline",
      );

      urlParams.delete("token");
      window.history.replaceState({}, "", window.location.pathname);
    }
    const token = localStorage.getItem("jwt_token");
    const platform = localStorage.getItem("platform");
    if (!token || !platform) {
      window.location.replace("/error");
    }
    setToken(token);
    setPlatform(platform);
  }, []);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("jwt_token");
    if (token) {
      const url = `${import.meta.env.VITE_API_HOST}/api/v1/platform/${platform}/card-setting`

      try {
        const response = await fetch(
          url,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.status !== 200) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        setFormValues(ConvertToEcType(data));
        setOriginalFormValues(ConvertToEcType(data));
      } catch (error) {
        const eventId = Sentry.captureException(error);
        window.location.replace(`/error?eventId=${eventId}`);
      }
    }
  }, [platform]);

  useEffect(() => {
    if (!token || !platform) return;

    fetchData().catch((error) => {
      console.error(error);
    });
  }, [fetchData, token, platform]);

  const uploadImage = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${import.meta.env.VITE_API_HOST}/api/v1/platform/upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      try {
        const data = await response.json();
        return data.object;
      } catch (error) {
        message.error(t("Upload failed! Please try again."));
        console.error(error);
        return "";
      }
    },
    [token],
  );

  const saveData = useCallback(async () => {
    if (!formValues) return;

    setSaveLoading(true);

    let logoURL = formValues.logo_url;

    const hasFileToUpload = formValues?.logo[0]?.originFileObj !== undefined;
    if (hasFileToUpload) {
      const uploadedLogoUrl = await uploadImage(
        formValues?.logo[0].originFileObj as File,
      );
      logoURL = uploadedLogoUrl;
    }

    //如果沒有取得logoURL，且有檔案要上傳，則不繼續執行
    if (hasFileToUpload && !logoURL) {
      setSaveLoading(false);
      return;
    }

    const submitData: ecType = {
      ...formValues,
      logo_url: logoURL,
    };

    const url = `${import.meta.env.VITE_API_HOST}/api/v1/platform/${platform}/card-setting`
    const response = await fetch(
      url,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(ConvertToBackendType(submitData)),
      },
    );
    if (response.status === 200) {
      fetchData();
    } else {
      message.error(t("Save failed! Please try again."));
    }

    setSaveLoading(false);
  }, [formValues, platform, token, fetchData, uploadImage]);

  const capitalizeFirstLetter = (string: string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const onManualSubmit = () => {
    ECFormRef.current?.handleManualSubmit();
  };

  return (
    formValues &&
    platform && (
      <Flex vertical className="lg:h-[99vh] lg:min-w-[1124px] max-w-[1400px]">
        <BrandLink brand={platform} />
        <Flex vertical className="bg-white border">
          <SubHeader
            title={`${capitalizeFirstLetter(platform)} Integration`}
            refreshData={() => setFormValues(originalFormValues)}
            saveData={onManualSubmit}
            saveLoading={saveLoading}
            enableSave={
              JSON.stringify(formValues) !== JSON.stringify(originalFormValues)
            }
          />
          <div className="flex flex-col min-h-[750px] lg:h-[75vh] px-6 py-3 lg:px-10 lg:py-5 lg:pb-10 lg:flex-row gap-6">
            <Flex className="mac-scrollbar lg:basis-1/2 lg:overflow-y-auto">
              <ECForm
                ref={ECFormRef}
                platform={platform}
                formValues={formValues}
                originalFormValues={originalFormValues}
                saveData={saveData}
                setFormValues={setFormValues}
              />
            </Flex>
            <Flex className="lg:basis-1/2 min-w-[300px]">
              <MemberPreview data={formValues} platform={platform} />
            </Flex>
          </div>
        </Flex>
      </Flex>
    )
  );
};
export default ECModule;
